import React, { useCallback } from "react";
import { useHistory } from "react-router-dom";
import { Button, Modal } from "react-bootstrap";
import styled from "styled-components";
import { marcarComoLida } from "@spark/services/notificacoes";
import {
  ehNotificacaoAtrasoCritico,
  obterLinkDestino,
  TIPO_ESPERA_ELEVADA,
} from "@spark/services/notificacoes/alerta";
import { useNotificacoesAlert } from "@/context/NotificacoesAlertContext";
import { colorGray } from "@spark/ui-components/theme";

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  const now = new Date();
  const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));

  if (diffInHours < 1) {
    return "Agora mesmo";
  }
  if (diffInHours < 24) {
    return `${diffInHours}h atras`;
  }
  return date.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const obterVarianteItem = (notificacao) => {
  if (ehNotificacaoAtrasoCritico(notificacao)) {
    return "critical";
  }
  if (notificacao?.TipoNotificacao === TIPO_ESPERA_ELEVADA) {
    return "warning";
  }
  return "default";
};

const DescargaAtrasoAlertModal = () => {
  const history = useHistory();
  const {
    modalAberto,
    atrasosNaoLidos,
    fecharModalAtrasos,
    removerAtrasoDaLista,
    recarregarAtrasosNaoLidos,
    sinalizarAtualizacaoNotificacoes,
  } = useNotificacoesAlert();

  const handleLer = useCallback(
    async (notificacaoId) => {
      try {
        await marcarComoLida(notificacaoId);
        removerAtrasoDaLista(notificacaoId);
      } catch {
        // Mantém item em caso de falha.
      }
    },
    [removerAtrasoDaLista]
  );

  const handleVerDescarga = useCallback(
    (notificacao) => {
      const link = obterLinkDestino(notificacao);
      fecharModalAtrasos();
      history.push(link);
    },
    [fecharModalAtrasos, history]
  );

  const handleMarcarTodas = useCallback(async () => {
    if (atrasosNaoLidos.length === 0) {
      return;
    }

    try {
      await Promise.all(atrasosNaoLidos.map((item) => marcarComoLida(item.Id)));
      await recarregarAtrasosNaoLidos();
      sinalizarAtualizacaoNotificacoes();
      fecharModalAtrasos();
    } catch {
      // Mantém lista atual em caso de falha.
    }
  }, [atrasosNaoLidos, fecharModalAtrasos, recarregarAtrasosNaoLidos, sinalizarAtualizacaoNotificacoes]);

  return (
    <Modal show={modalAberto} onHide={fecharModalAtrasos} centered size="lg">
      <Modal.Header closeButton style={{ background: "#fff8f0", borderBottom: "1px solid #f0d9c4" }}>
        <Modal.Title style={{ fontSize: "16px", color: "#a35c00" }}>
          Alertas de descarga ({atrasosNaoLidos.length})
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: 0, maxHeight: "420px", overflowY: "auto" }}>
        {atrasosNaoLidos.length === 0 ? (
          <EmptyState>Nenhum alerta pendente.</EmptyState>
        ) : (
          atrasosNaoLidos.map((notificacao) => {
            const variante = obterVarianteItem(notificacao);
            return (
              <Item key={notificacao.Id} $variante={variante}>
                <ItemContent>
                  <ItemHeader>
                    <ItemTitle $variante={variante}>{notificacao.Titulo}</ItemTitle>
                    <ItemDate>{formatDate(notificacao.DataCriacao)}</ItemDate>
                  </ItemHeader>
                  <ItemMessage>{notificacao.Mensagem || ""}</ItemMessage>
                </ItemContent>
                <ItemActions>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handleVerDescarga(notificacao)}
                  >
                    Ver descarga
                  </Button>
                  <Button variant="primary" size="sm" onClick={() => handleLer(notificacao.Id)}>
                    Ler
                  </Button>
                </ItemActions>
              </Item>
            );
          })
        )}
      </Modal.Body>
      <Modal.Footer>
        {atrasosNaoLidos.length > 0 && (
          <Button variant="outline-secondary" onClick={handleMarcarTodas}>
            Marcar todas como lidas
          </Button>
        )}
        <Button variant="secondary" onClick={fecharModalAtrasos}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DescargaAtrasoAlertModal;

const EmptyState = styled.div`
  padding: 20px 16px;
  font-size: 13px;
  color: ${colorGray};
`;

const Item = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid #eee;
  background: ${({ $variante }) => {
    if ($variante === "critical") return "#fff8f8";
    if ($variante === "warning") return "#fffbeb";
    return "transparent";
  }};
`;

const ItemContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const ItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
`;

const ItemTitle = styled.p`
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: ${({ $variante }) => {
    if ($variante === "critical") return "#c0392b";
    if ($variante === "warning") return "#b45309";
    return "#333";
  }};
  word-break: break-word;
`;

const ItemDate = styled.span`
  flex-shrink: 0;
  font-size: 10px;
  color: ${colorGray};
  white-space: nowrap;
`;

const ItemMessage = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: 1.45;
  color: ${colorGray};
  word-break: break-word;
  white-space: pre-wrap;
`;

const ItemActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex-shrink: 0;
`;
